import { LabHowItWorks } from "./LabHowItWorks";
import type { LabRecord } from "./labBiomarkers";
import { LabResultCard } from "./LabResults";
export { LabResultCard } from "./LabResults";
import { LabPdfDownload } from "./LabPdfDownload";
import { LabKitProgress } from "./LabKitProgress";
import { useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import {
  ArrowRight,
  FileText,
  FlaskConical,
  MessageCircle,
  Package,
  Truck,
  X,
} from "lucide-react";
import { Link } from "react-router-dom";
import { SanityImage } from "@/components/content/SanityImage";
import { Badge, Button } from "@/design-system/primitives";
import { useLabReport } from "@/lib/api/labHooks";
import type { LabOrder, LabTracking } from "@/types/labs";
import {
  labDate,
  labDisplayName,
  labMoney,
  labOrderLabel,
  labProductImage,
  labReportUrl,
  labStatusLabel,
  labTrackingUrl,
} from "./labPresentation";

export function LabSkeleton({
  label = "Fetching lab order",
}: {
  label?: string;
}) {
  return (
    <div
      role="status"
      aria-label={label}
      className="flex flex-col gap-15 py-20"
    >
      <div className="h-100 bg-raised motion-safe:animate-pulse" />
      <div className="h-[24rem] bg-raised motion-safe:animate-pulse" />
    </div>
  );
}

export function LabError({
  message,
  onRetry,
}: {
  message: string;
  onRetry?: () => void;
}) {
  return (
    <div className="border border-line-subtle p-25 flex flex-col items-start gap-15">
      <p role="alert" className="type-body-md text-ink-secondary">
        {message}
      </p>
      {onRetry && (
        <Button variant="secondary" onClick={onRetry}>
          Try again
        </Button>
      )}
    </div>
  );
}

export function LabOrderContent({
  order,
  clinician = false,
  adminPatientId,
  onReportOpen,
  records = [],
  initialMarker,
}: {
  order: LabOrder;
  records?: LabRecord[];
  initialMarker?: { resultId: string; index: number };
  onReportOpen?: () => void;
  clinician?: boolean;
  adminPatientId?: string;
}) {
  const [reportResult, setReportResult] = useState<string | null>(null);
  const resultsSection = (
    <section id={clinician ? undefined : "results"} className="scroll-mt-80">
      <div className="flex flex-wrap items-center justify-between gap-10 mb-12">
        <h2 className="type-label-md">Your lab results</h2>
        <span className="type-label-xs text-ink-muted">
          {order.results.length} report{" "}
          {order.results.length === 1 ? "record" : "records"}
        </span>
      </div>
      {order.results.length === 0 ? (
        <div className="border border-line-subtle bg-raised p-25">
          <FileText size={22} aria-hidden className="text-ink-muted mb-15" />
          <h3 className="font-display text-20">
            {order.fulfillment_status === "resultsReady"
              ? "Your report is being retrieved"
              : "No lab results yet"}
          </h3>
          <p className="type-body-md text-ink-secondary leading-145 mt-10">
            {order.fulfillment_status === "resultsReady"
              ? "Tasso has marked the order ready. We are waiting for the actual laboratory report before displaying results."
              : "Your laboratory results and original report will appear here once they are available."}
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-25">
          {order.results.map((result) => (
            <LabResultCard
              key={result.id}
              result={result}
              records={records}
              clinical={clinician || !!adminPatientId}
              initialMarker={
                initialMarker?.resultId === result.id
                  ? initialMarker.index
                  : undefined
              }
              download={
                result.report_available ? (
                  <LabPdfDownload
                    orderId={order.id}
                    resultId={result.id}
                    clinician={clinician}
                    adminPatientId={adminPatientId}
                  />
                ) : undefined
              }
              onReport={() => {
                onReportOpen?.();
                setReportResult(result.id);
              }}
            />
          ))}
        </div>
      )}
    </section>
  );
  return (
    <div className="ph-no-capture flex flex-col gap-25">
      <section className="border border-line-subtle bg-raised">
        <div className="flex flex-wrap items-start justify-between gap-15 p-20 sm:p-25">
          <div className="flex items-start gap-15">
            {labProductImage(order.panel_code) ? (
              <SanityImage
                url={labProductImage(order.panel_code)}
                alt="System Labs at-home blood collection device"
                width={160}
                height={160}
                className="h-80 w-80 shrink-0 object-contain mix-blend-multiply"
              />
            ) : (
              <FlaskConical
                aria-hidden
                className="text-accent shrink-0"
                size={28}
                strokeWidth={1.25}
              />
            )}
            <div>
              <p className="type-label-xs text-ink-muted">
                At-home blood testing{clinician ? ` · ${order.panel_code}` : ""}
              </p>
              <h2 className="font-display text-24 leading-110 mt-10">
                {labDisplayName(order.product_name)}
              </h2>
              <p className="type-body-sm text-ink-secondary mt-10">
                Ordered {labDate(order.created_at)} ·{" "}
                {labMoney(order.amount_paid_cents, order.currency)}
              </p>
            </div>
          </div>
          <Badge
            tone={
              order.recollection_message || order.issue ? "warning" : "neutral"
            }
          >
            {labOrderLabel(order)}
          </Badge>
        </div>
      </section>

      {(order.issue || order.recollection_message) && (
        <section className="border border-line-subtle border-l-4 border-l-warning bg-warning-wash p-20">
          <h3 className="type-label-sm">Your next step</h3>
          <p className="type-body-md leading-145 mt-10">
            {order.recollection_message || order.issue}
          </p>
          {!clinician && (
            <Link
              to="/app/clinic"
              className="inline-flex items-center gap-8 min-h-44 underline text-14 mt-10"
            >
              Contact care team <ArrowRight size={14} />
            </Link>
          )}
        </section>
      )}

      {!clinician &&
        !order.results.length &&
        !order.issue &&
        !order.recollection_message &&
        ["atPatient", "inTransitToLab", "atLab"].includes(
          order.fulfillment_status,
        ) && (
          <section className="border border-line-subtle bg-raised p-20">
            <h2 className="font-display text-20">
              {order.fulfillment_status === "atPatient"
                ? "Ready to collect your sample."
                : order.fulfillment_status === "inTransitToLab"
                  ? "Your sample is on its way."
                  : "Your sample is with the laboratory."}
            </h2>
            <p className="type-body-md text-ink-secondary leading-145 mt-10">
              {order.fulfillment_status === "atPatient"
                ? "Follow the preparation, collection and return instructions included with your kit. You can review the three-step overview here."
                : order.fulfillment_status === "inTransitToLab"
                  ? "Follow the return tracking below. Your results will appear here after the laboratory processes your sample."
                  : "Your results and downloadable laboratory report will appear here when they are available."}
            </p>
            {order.fulfillment_status === "atPatient" && (
              <LabHowItWorks source="delivered_kit" className="mt-10" />
            )}
          </section>
        )}
      {!!order.results.length && resultsSection}
      <section aria-label="Kit journey">
        <div className="flex items-center gap-10 mb-12">
          <Package size={18} aria-hidden />
          <h2 className="type-label-md">Your kit journey</h2>
        </div>
        <div className="mb-20">
          <LabKitProgress order={order} />
        </div>
        {order.bundles.length === 0 ? (
          <div className="border border-line-subtle p-20">
            <p className="type-body-md text-ink-secondary">
              {order.fulfillment_status === "awaiting_authorization" ||
              order.authorization_status === "declined"
                ? "This order is on hold. Contact your care team in Clinic for an update."
                : order.payment_status === "paid" ||
                    order.payment_status === "succeeded"
                  ? "Your order is being prepared. Tracking will appear here when your kit ships."
                  : "Kit preparation starts once your payment is confirmed."}
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-15">
            {order.bundles.map((bundle, index) => (
              <article key={bundle.id} className="border border-line-subtle">
                <div className="flex flex-wrap items-center justify-between gap-10 p-15 bg-raised">
                  <h3 className="type-label-sm">Collection kit {index + 1}</h3>
                  <Badge>{labStatusLabel(bundle.status)}</Badge>
                </div>
                <div className="grid sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-line-subtle">
                  <TrackingCard
                    title="To your address"
                    tracking={bundle.outbound_tracking}
                  />
                  <TrackingCard
                    title="Back to the laboratory"
                    tracking={bundle.return_tracking}
                  />
                </div>
                <div className="border-t border-line-subtle px-15 py-12 type-body-sm text-ink-muted">
                  Last update: {labDate(bundle.status_changed_at)}
                  {bundle.specimen_ids.length > 0 && (
                    <span className="block mt-6 break-all">
                      Specimen {bundle.specimen_ids.join(", ")}
                    </span>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}
        {order.shipping_address && (
          <div className="mt-12 type-body-sm text-ink-secondary leading-145">
            <span className="type-label-xs text-ink-muted">
              Delivery address
            </span>
            <p className="mt-6">
              {order.shipping_address.line1}
              {order.shipping_address.line2
                ? `, ${order.shipping_address.line2}`
                : ""}
              <br />
              {order.shipping_address.city}, {order.shipping_address.state}{" "}
              {order.shipping_address.postal_code}
            </p>
          </div>
        )}
      </section>

      {!order.results.length && resultsSection}
      {!clinician && (
        <section className="border-y border-line-subtle py-20 flex flex-col sm:flex-row sm:items-center justify-between gap-15">
          <div>
            <h2 className="font-display text-20">
              {order.results.length
                ? "Talk through your results"
                : "Questions about your test?"}
            </h2>
            <p className="type-body-md text-ink-secondary mt-8 leading-140">
              {order.results.length
                ? "Your care team can help you understand your report and next steps."
                : "Your care team can help with your kit, returning your sample and what happens next."}
            </p>
          </div>
          <Button variant="secondary" asChild>
            <Link to="/app/clinic">
              <MessageCircle size={16} />
              Contact care team
            </Link>
          </Button>
        </section>
      )}
      <LabReportDialog
        key={`${order.id}:${reportResult ?? ""}`}
        orderId={order.id}
        resultId={reportResult}
        reportCount={
          order.results.find((result) => result.id === reportResult)
            ?.report_count ?? 1
        }
        clinician={clinician}
        adminPatientId={adminPatientId}
        onClose={() => setReportResult(null)}
      />
    </div>
  );
}

function TrackingCard({
  title,
  tracking,
}: {
  title: string;
  tracking: LabTracking | null;
}) {
  const url = tracking ? labTrackingUrl(tracking) : null;
  return (
    <div className="p-20">
      <div className="flex items-center gap-8 type-label-xs text-ink-muted">
        <Truck size={15} aria-hidden />
        {title}
      </div>
      {tracking?.tracking_number ? (
        <>
          <p className="type-body-md mt-12 break-all">
            {tracking.carrier || "Carrier"} · {tracking.tracking_number}
          </p>
          {url && (
            <a
              className="inline-flex items-center gap-8 min-h-40 mt-8 text-14 underline"
              href={url}
              target="_blank"
              rel="noopener noreferrer"
            >
              Track package <ArrowRight size={14} />
            </a>
          )}
        </>
      ) : (
        <p className="type-body-sm text-ink-secondary mt-12">
          Tracking not yet available
        </p>
      )}
    </div>
  );
}

export function LabReportDialog({
  orderId,
  resultId,
  reportCount,
  clinician,
  adminPatientId,
  onClose,
}: {
  orderId: string;
  resultId: string | null;
  reportCount: number;
  clinician: boolean;
  adminPatientId?: string;
  onClose: () => void;
}) {
  const [reportIndex, setReportIndex] = useState(0);
  const query = useLabReport(
    orderId,
    resultId,
    clinician,
    reportIndex,
    adminPatientId,
  );
  const downloadUrl = labReportUrl(query.data?.download_url);
  return (
    <Dialog.Root
      open={Boolean(resultId)}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-ink/40 z-[110]" />
        <Dialog.Content className="ph-no-capture fixed inset-15 md:inset-40 z-[111] bg-page border border-line-subtle flex flex-col overflow-hidden">
          <div className="p-20 border-b border-line-subtle flex items-start justify-between gap-15">
            <div>
              <Dialog.Title className="font-display text-24">
                {query.data?.title || "Original laboratory report"}
              </Dialog.Title>
              <Dialog.Description className="type-body-sm text-ink-secondary mt-8">
                Your private report from the performing laboratory.
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <Button variant="icon" aria-label="Close report">
                <X size={20} />
              </Button>
            </Dialog.Close>
          </div>
          {resultId && (
            <div className="px-20 py-12 border-b border-line-subtle">
              <LabPdfDownload
                orderId={orderId}
                resultId={resultId}
                reportIndex={reportIndex}
                clinician={clinician}
                adminPatientId={adminPatientId}
              />
            </div>
          )}
          {reportCount > 1 && (
            <label className="p-20 border-b border-line-subtle flex flex-wrap items-center gap-15 text-14">
              Source document
              <select
                className="border border-line-subtle min-h-44 px-12 bg-page"
                value={reportIndex}
                onChange={(event) => setReportIndex(Number(event.target.value))}
              >
                {Array.from({ length: reportCount }, (_, index) => (
                  <option key={index} value={index}>
                    Report {index + 1} of {reportCount}
                  </option>
                ))}
              </select>
            </label>
          )}
          <div className="p-20 overflow-auto flex-1">
            {query.isLoading ? (
              <LabSkeleton label="Fetching private report" />
            ) : query.isError ? (
              <LabError
                message="We could not open this report. Refresh and try again."
                onRetry={() => void query.refetch()}
              />
            ) : query.data?.content_text ? (
              <pre className="font-mono text-12 leading-150 whitespace-pre-wrap break-words">
                {query.data.content_text}
              </pre>
            ) : downloadUrl ? (
              <div className="flex flex-col items-start gap-15">
                <p className="type-body-md">
                  Your report is ready to open in a new tab.
                </p>
                <Button asChild>
                  <a
                    href={downloadUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Open original report <ArrowRight size={14} />
                  </a>
                </Button>
              </div>
            ) : (
              <p className="type-body-md">
                The original report is not available yet.
              </p>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
